import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../api/ApiProvider";
import { documentKeys } from "./documents.keys";
import { SaveRxFromExtractionDto, SaveRxResponse } from "../../api-client";
import { rxKeys } from "../Rx/rx.keys";
import { appointmentKeys } from "../Appointment/appointment.keys";
import { transactionKeys } from "../Transactions/transaction.keys";
import { calendarKeys, invalidateCalendarAppointmentCaches } from "../Calendar/calendar.keys";
import useStore from "../../helpers/useStore";
import { PatientsContext } from "../Patients/patients.provider";

/**
 * NOTE: Global error/success handling is configured in src/stores/queryClient.ts
 * - MutationCache handles automatic error notifications via notificationStore
 * - MutationCache handles automatic success notifications via meta.successMessage
 */

// ============================================================================
// Extract Document OCR Mutation
// ============================================================================

/**
 * Hook to trigger OCR extraction for a document.
 */
export const useExtractDocumentOcr = () => {
  const { documents } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["documents", "extractOcr"],
    mutationFn: async (documentId: string) => {
      const response = await documents.extractDocumentOcr(documentId);
      return response;
    },
    onSuccess: (_result, documentId) => {
      // Invalidate OCR-related queries to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: documentKeys.rxExtraction.byDocument(documentId),
      });
      queryClient.invalidateQueries({
        queryKey: documentKeys.rawOcr.byDocument(documentId),
      });
    },
    meta: {
      successMessage: "OCR-Extraktion erfolgreich gestartet",
      errorMessage: "Fehler beim Starten der OCR-Extraktion",
    },
  });
};

// ============================================================================
// Save Rx From Extraction Mutation
// ============================================================================

/**
 * Hook to save/update an Rx from extraction data.
 * Invalidates the same queries as useSaveRxs since it creates/updates an Rx.
 */
export const useSaveRxFromExtraction = () => {
  const { documents } = useApi();
  const queryClient = useQueryClient();
  const patientStore = useStore(PatientsContext);

  type Vars = {
    documentId: string;
    patientId: number;
    data: SaveRxFromExtractionDto;
  };

  return useMutation({
    mutationKey: ["documents", "saveRxFromExtraction"],
    mutationFn: async ({ documentId, data }: Vars) => {
      const response = await documents.saveRxFromExtraction(documentId, data);
      return response as SaveRxResponse[];
    },
    onSuccess: async (_result, variables) => {
      const { documentId, patientId } = variables;
      // SaveRxResponse has { rx: RxDto, therapies, validRxSeriesIds }
      const rxId = _result?.[0]?.rx?.id;

      // Check if we should open preflight dialog (same as useSaveRxs)
      if (_result && _result[0]?.validRxSeriesIds && _result[0]?.validRxSeriesIds.length > 0) {
        patientStore.setPreflightDialogOpen(true, _result[0]?.validRxSeriesIds || []);
      }

      // Invalidate the same queries as useSaveRxs
      await Promise.all([
        // Document-specific queries
        queryClient.invalidateQueries({
          queryKey: documentKeys.rxExtraction.byDocument(documentId),
        }),
        // Rx queries
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxList.forPatient(patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxSeriesList.forPatient(patientId),
          refetchType: "active",
        }),
        ...(rxId ? [
          queryClient.invalidateQueries({
            queryKey: rxKeys.rx.detail(rxId),
            refetchType: "active",
          }),
        ] : []),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxLackingAppointments.forPatient(patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.therapyRxSeries.forPatient(patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxSeries.all(),
          refetchType: "active",
        }),
        // Transaction queries
        queryClient.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(patientId),
          refetchType: "active",
        }),
        // Appointment queries
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsBySeries.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.continuousAppointmentsByPatient.detail(patientId.toString()),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointmentsByPatient.detail(patientId),
        }),
        ...(rxId ? [
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.appointmentsByRx.detail(rxId),
          }),
        ] : []),
        // Calendar queries
        queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledPatientAppointments.detail(patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledContinuousAppointments.detail(patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.continuousAppointmentsForPatient.detail(patientId),
        }),
        invalidateCalendarAppointmentCaches(queryClient),
      ]);
    },
    meta: {
      successMessage: "Rezept erfolgreich gespeichert",
      errorMessage: "Fehler beim Speichern des Rezepts",
    },
  });
};

// ============================================================================
// Assign Document to Patient Mutation (Inbox)
// ============================================================================

/**
 * Hook to assign an inbox document to a patient.
 * This moves the blob from the inbox path to the patient's folder
 * and sets patientId on the Document.
 */
export const useAssignDocumentToPatient = () => {
  const { documents } = useApi();
  const queryClient = useQueryClient();

  type Vars = {
    documentId: string;
    patientId: number;
  };

  return useMutation({
    mutationKey: ["documents", "assignToPatient"],
    mutationFn: async ({ documentId, patientId }: Vars) => {
      return await documents.assignDocumentToPatient(documentId, { patientId });
    },
    meta: {
      successMessage: "Dokument dem Patienten zugeordnet",
      errorMessage: "Fehler beim Zuordnen des Dokuments",
    },
  });
};

export const useFinalizeInboxDocument = () => {
  const { documents } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["documents", "finalizeInbox"],
    mutationFn: async (documentId: string) => {
      return await documents.finalizeInboxDocument(documentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.inbox.all() });
    },
    meta: {
      successMessage: "Dokument aus Eingangskorb verschoben",
      errorMessage: "Fehler beim Verschieben des Dokuments",
    },
  });
};
